import { useState } from "react";
import { X, CalendarDays, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { PROJECTIONS_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import dayjs from "dayjs";

const PROJ_API = `/${PROJECTIONS_URL}`;

const formatINR = (v) => {
    const n = Number(v);
    if (!n) return "₹0";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
};

/**
 * ProjectionModal — Commit or Reschedule a sales projection
 *
 * Props:
 *   mode: "commit" | "reschedule"
 *   deal: { customer_id, customer_name, pipeline_value, expected_closing_date }  (for commit)
 *   projection: { id, projected_value, projected_month, customer_name }          (for reschedule)
 *   onClose: () => void
 *   onSuccess: () => void
 */
const ProjectionModal = ({ mode = "commit", deal, projection, onClose, onSuccess }) => {
    const { create } = useApiMethods();

    const isReschedule = mode === "reschedule";
    const customerName = isReschedule ? projection?.customer_name : deal?.customer_name;
    const currentValue = isReschedule ? projection?.projected_value : deal?.pipeline_value;

    const defaultMonth = isReschedule
        ? ""
        : deal?.expected_closing_date
            ? dayjs(deal.expected_closing_date).format("YYYY-MM")
            : dayjs().format("YYYY-MM");

    const [month, setMonth] = useState(defaultMonth);
    const [value, setValue] = useState(currentValue || "");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!month) {
            ToastMsg("error", "Please select a month");
            return;
        }

        setLoading(true);
        try {
            if (isReschedule) {
                const res = await create(`${PROJ_API}${projection.id}/reschedule/`, {
                    new_month: `${month}-01`,
                    notes,
                });
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Projection rescheduled");
                    onSuccess?.();
                    onClose();
                } else {
                    ToastMsg("error", res?.message || "Failed to reschedule");
                }
            } else {
                const res = await create(PROJ_API, {
                    customer_id: deal.customer_id,
                    projected_value: Number(value),
                    projected_month: `${month}-01`,
                    notes,
                });
                if (res?.status === "success") {
                    ToastMsg("success", "Projection committed");
                    onSuccess?.();
                    onClose();
                } else {
                    ToastMsg("error", res?.message || "Failed to commit projection");
                }
            }
        } catch {
            ToastMsg("error", "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-indigo-500" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {isReschedule ? "Reschedule Projection" : "Commit Sales Projection"}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X className="h-4 w-4 text-gray-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Customer info */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Customer</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{customerName || "—"}</p>
                        {isReschedule && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                <span>Current month: <span className="font-medium text-gray-700 dark:text-gray-300">{dayjs(projection?.projected_month).format("MMM YYYY")}</span></span>
                                <ArrowRight className="h-3 w-3" />
                                <span>Value: <span className="font-medium text-indigo-600">{formatINR(projection?.projected_value)}</span></span>
                            </div>
                        )}
                    </div>

                    {/* Value (only for commit, not reschedule — rescheduled keeps original value) */}
                    {!isReschedule && (
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Projected Value (₹)
                            </label>
                            <input
                                type="number"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="e.g. 500000"
                                required
                                min="1"
                                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            {value && (
                                <p className="mt-1 text-xs text-gray-400">{formatINR(value)}</p>
                            )}
                        </div>
                    )}

                    {/* Month picker */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {isReschedule ? "New Target Month" : "Target Month"}
                        </label>
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            required
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Notes (optional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder={isReschedule ? "Reason for rescheduling..." : "Any context about this commitment..."}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" size="sm" onClick={onClose} className="text-xs">
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={loading} className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
                            {loading ? "Saving..." : isReschedule ? "Reschedule" : "Commit Projection"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProjectionModal;
